import { InputGroup } from "@/_components/lib/ui/input-group";
import { Tooltip } from "@/_components/lib/ui/tooltip";
import { useSearchPost } from "@/_services/hooks/useSearchPost";
import { blogLinkBuilder } from "@/_utils";
import { Box, Icon, Input, Text, Separator, Skeleton } from "@chakra-ui/react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { HiMiniMagnifyingGlass } from "react-icons/hi2";
import { useTranslations } from "next-intl";

export const SearchBox = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [keywords, setKeywords] = useState<string>("");
  const { searchedPosts, searchPostFn, loading } = useSearchPost();
  const t = useTranslations("landing.blog.search");

  useEffect(() => {
    if (keywords.length > 2) {
      const delayDebounceFn = setTimeout(() => {
        searchPostFn(keywords);
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [keywords]);

  const renderSearchedPosts = () => {
    if (keywords.length < 3) {
      return (
        <Box p={2} textStyle="sm">
          {t("min_characters")}
        </Box>
      );
    }
    if (loading) {
      return Array.from({ length: 5 }).map((_, index) => (
        <Skeleton mt="2" key={index} h="4rem" borderRadius="md" />
      ));
    }
    if (searchedPosts.length === 0) {
      return <Box p={2}>{t("no_results")}</Box>;
    }
    return searchedPosts.map((post) => (
      <Tooltip
        content={post.title}
        key={post._id}
        openDelay={100}
        showArrow
        positioning={{ placement: "top" }}
      >
        <Link href={blogLinkBuilder(post.slug.current)}>
          <Box
            key={post._id}
            fontWeight={600}
            _hover={{ bgColor: "neutral.100" }}
            p={2}
            borderRadius="md"
            onClick={() => setKeywords("")}
          >
            <Text truncate color="primary.100">
              {post.title}
            </Text>
            <Text fontSize="sm">
              {post.author.name} - #
              {post.categories?.length
                ? post.categories[0].name
                : t("uncategorized")}
            </Text>
          </Box>
        </Link>
      </Tooltip>
    ));
  };
  return (
    <Box pos="relative" height="auto">
      <InputGroup
        ref={ref}
        endElement={
          <Icon boxSize={5} fill={"primary.600"}>
            <HiMiniMagnifyingGlass />
          </Icon>
        }
        w="16rem"
      >
        <Input
          border="2px solid"
          borderColor="neutral.100"
          borderRadius="50px"
          placeholder={t("placeholder")}
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          _focusVisible={{ outlineColor: "primary.600" }}
          textStyle="xs"
          size="sm"
        />
      </InputGroup>
      {keywords.length >= 1 && (
        <Box
          pos="absolute"
          left={0}
          minH="5rem"
          bg="white"
          borderRadius="2xl"
          mt={2}
          shadow="xl"
          w={ref.current?.clientWidth || "20rem"}
          zIndex={100}
          p={4}
          transformOrigin="bottom"
        >
          <Text fontWeight={600} ml={2} mb={2} textStyle="lg">
            {t("result_title")}
          </Text>
          <Separator />
          {renderSearchedPosts()}
        </Box>
      )}
    </Box>
  );
};
